import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './allVideos.module.css';
import Header from '../../components/standartComponents/header';
import { getVideoLessons } from '../../lib/videoLessonApi';
import { getVideoCategories } from '../../lib/videoCategoryApi';
import { getLanguageLevels } from '../../lib/getLanguageLevels';
import { getLearningPaths } from '../../lib/learningPath';
import { toggleVideoFavorite, getAllVideoFavorites } from '../../lib/favoriteApi';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';

const levelNumberMap = {
  7: 'pre-A1', 1: 'A1', 2: 'A2', 3: 'B1', 4: 'B2', 5: 'C1', 6: 'C2'
};

const AllVideos = ({ authUser }) => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  
  const [reportModal, setReportModal] = useState({ open: false, id: null });
  const [reportOpt, setReportOpt] = useState(null);
  const [toast, setToast] = useState(false);
  const [learningPaths, setLearningPaths] = useState([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(false);
  const [likedVideos, setLikedVideos] = useState({}); // { [videoId]: { liked: boolean, favoriteId: number|null } }
  const [dislikedVideos, setDislikedVideos] = useState({}); // { [videoId]: boolean }
  const [isTogglingFavorite, setIsTogglingFavorite] = useState({}); // { [videoId]: boolean }
  const { t } = useTranslation();


  // Helper function to get all child categories (including nested)
  const getAllChildCategories = (parentId, allCats) => {
    const children = allCats.filter((cat) => cat.parent === parentId);
    let allIds = children.map((cat) => cat.id);
    children.forEach((child) => {
      allIds = [...allIds, ...getAllChildCategories(child.id, allCats)];
    });
    return allIds;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (authUser?.isAuthenticated) {
        setIsLoadingPaths(true);
      }
      try {
        const [lessonsRes, catRes, levelRes, pathsRes] = await Promise.all([
          getVideoLessons(),
          getVideoCategories(),
          getLanguageLevels(),
          authUser?.isAuthenticated ? getLearningPaths() : null
        ]);
        setVideos(lessonsRes.results || []);
        // Handle both paginated {results:[]} and array response
        const catData = catRes.results || catRes || [];
        setCategories(Array.isArray(catData) ? catData : []);
        if (levelRes) {
          const lvlData = levelRes.results || levelRes || [];
          setAllLevels(Array.isArray(lvlData) ? lvlData : []);
        }
        if (pathsRes) setLearningPaths(pathsRes.results || []);

        // Fetch favorite statuses for all videos if user is authenticated
        if (authUser?.isAuthenticated) {
          try {
            const favRes = await getAllVideoFavorites();
            const likedMap = {};
            favRes.results?.forEach((fav) => {
              likedMap[fav.lesson] = { liked: true, favoriteId: fav.id };
            });
            setLikedVideos(likedMap);
          } catch (err) {
            console.error('Error fetching favorites:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
        setIsLoadingPaths(false);
      }
    };
    fetchData();
  }, [authUser?.isAuthenticated]);

  const getCategoryName = (catId) => {
    const cat = categories.find(c => String(c.id) === String(catId));
    return cat ? cat.name : '';
  };

  const getLevelName = (levelId) => {
    const lvl = allLevels.find(l => Number(l.id) === Number(levelId));
    return lvl ? lvl.name : '';
  };

  const toggleFav = async (id) => {
    if (isTogglingFavorite[id]) return;
    const current = likedVideos[id] || { liked: false, favoriteId: null };

    // Optimistic update (always update UI first)
    setLikedVideos(prev => ({
      ...prev,
      [id]: { liked: !current.liked, favoriteId: current.favoriteId }
    }));

    // Only make API call if user is authenticated
    if (authUser?.isAuthenticated) {
      setIsTogglingFavorite(prev => ({ ...prev, [id]: true }));
      try {
        const response = await toggleVideoFavorite(id, current.favoriteId, current.liked);
        if (!current.liked) {
          setLikedVideos(prev => ({
            ...prev,
            [id]: { liked: true, favoriteId: response.id }
          }));
        } else {
          setLikedVideos(prev => ({
            ...prev,
            [id]: { liked: false, favoriteId: null }
          }));
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
        // Revert optimistic update only if API call failed
        setLikedVideos(prev => ({
          ...prev,
          [id]: current
        }));
      } finally {
        setIsTogglingFavorite(prev => ({ ...prev, [id]: false }));
      }
    }
  };

  const setLike = (id, val) => {
    if (val === 1) {
      toggleFav(id);
      if (dislikedVideos[id]) {
        setDislikedVideos(prev => ({ ...prev, [id]: false }));
      }
    } else if (val === -1) {
      setDislikedVideos(prev => ({ ...prev, [id]: !prev[id] }));
      if (likedVideos[id]?.liked) {
        toggleFav(id);
      }
    }
  };

  const resetFilters = () => {
    setSearch('');
    setFilterLevel('');
    setFilterTopic('');
  };

  const submitReport = () => {
    setReportModal({ open: false, id: null });
    setReportOpt(null);
    setToast(true);
    setTimeout(() => setToast(false), 3000);
  };

  const filteredVideos = videos.filter(v => {
    const q = search.toLowerCase();
    const name = v.video?.name?.toLowerCase() || '';
    
    let catNames = [];
    if (Array.isArray(v.video?.category)) {
      catNames = v.video.category.map(catId => getCategoryName(catId).toLowerCase());
    } else {
      catNames = [getCategoryName(v.video?.category).toLowerCase()];
    }
    
    if (q && !name.includes(q) && !catNames.some(cn => cn.includes(q))) return false;
    
    if (filterLevel) {
      const lvlName = getLevelName(v.video?.languageLevel);
      if (lvlName !== filterLevel) return false;
    }
    
    if (filterTopic) {
      // Find the parent category by name
      const selectedParentCat = categories.find(cat => cat.name === filterTopic && cat.parent === null);
      if (selectedParentCat) {
        // Get all child categories of this parent
        const childIds = getAllChildCategories(selectedParentCat.id, categories);
        // Check if video's category is either the parent or one of the children
        const videoCatId = v.video?.category;
        if (![selectedParentCat.id, ...childIds].includes(videoCatId)) return false;
      } else {
        // If no parent found, just match by name (fallback)
        const cat = getCategoryName(v.video?.category);
        if (cat !== filterTopic) return false;
      }
    }
    
    return true;
  });

  return (
    <div className={classes.main}>
      <Header authUser={authUser} />
      <div className={classes.gridBg}></div>
      <div className={classes.wrap}>
        
        {/* HERO */}
        <div className={classes.hero}>
          <h1 className={classes.heroTitle}>{t("Learn without a plan")}</h1>
          <p className={classes.heroSub}>{t("Choose any videos, complete lessons at your own pace, and practice speaking with AI")}</p>
          <p className={classes.heroNote}>{t("There is no fixed order in this mode — you choose what to learn.")}</p>
          <div className={classes.flow}>
            <div className={classes.flowNode}><div className={`${classes.fd} ${classes.fdV}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>{t("Video")}</div>
            <span className={classes.flowArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>
            <div className={classes.flowNode}><div className={`${classes.fd} ${classes.fdW}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg></div>{t("Words")}</div>
            <span className={classes.flowArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>
            <div className={classes.flowNode}><div className={`${classes.fd} ${classes.fdQ}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg></div>{t("Quiz")}</div>
            <span className={classes.flowArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>
            <div className={classes.flowNode}><div className={`${classes.fd} ${classes.fdS}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg></div>{t("Speaking")}</div>
          </div>
        </div>

        {/* CTA ROW */}
        <div className={classes.ctaRow}>
          <div  className={`${classes.ctaCard} ${classes.planCta}`} onClick={() => {
            if (!authUser?.isAuthenticated) {
              navigate('/survey');
            } else if (isLoadingPaths) {
              // Do nothing while loading
              return;
            } else if (learningPaths.length === 0) {
               navigate('/survey');
            } else {
              navigate('/create-course');
            }
          }} style={{ cursor: isLoadingPaths ? 'not-allowed' : 'pointer' }}>
            <div className={`${classes.ctaIcon} ${classes.ciPlan}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
            <div className={classes.ctaBody}><h3>{t("Would you like a structured learning plan?")}</h3><p>{t("Get a personal plan tailored to your goal and level")}</p></div>
            <button style={{ cursor: isLoadingPaths ? 'not-allowed' : 'pointer' }} className={`${classes.ctaBtn} ${classes.ctaBtnPrimary}`}>{t("Create my plan")}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></button>
          </div>
          <div className={`${classes.ctaCard} ${classes.teacherCta}`}>
            <div className={`${classes.ctaIcon} ${classes.ciTeach}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"/><rect width="18" height="18" x="3" y="4" rx="2"/><circle cx="12" cy="10" r="2"/></svg></div>
            <div className={classes.ctaBody}><h3>{t("Learn with a teacher")} <span className={classes.soonBadge}>{t("Soon")}</span></h3><p>{t("A teacher will help you practice words, pronunciation, and video dialogues.")}</p></div>
          </div>
        </div>

        {/* FILTERS */}
        <div className={classes.filterBar}>
          <div className={classes.searchBox}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="text" placeholder={t("Search videos")} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className={classes.filterSelect} value={filterLevel} onChange={e => setFilterLevel(e.target.value)}>
            <option value="">{t("Level")}: {t("All")}</option>
            {allLevels.map(lvl => (
              <option key={lvl.id} value={lvl.name}>{lvl.name}</option>
            ))}
          </select>
          <select className={classes.filterSelect} value={filterTopic} onChange={e => setFilterTopic(e.target.value)}>
            <option value="">{t("Topics")}: {t("All")}</option>
            {categories.filter(cat => cat.parent === null).map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
          <button className={classes.resetLink} onClick={resetFilters}>{t("Clear filters")}</button>
        </div>
        
        <div className={classes.infoBar}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
          {t("This is self-study: you choose the videos yourself. To follow a structured path, create a personal plan.")}
        </div>

        {/* VIDEO GRID */}
        <div className={classes.sectionLabel}>{t("All Videos")} <span className={classes.cnt}>{filteredVideos.length}</span></div>
        
        <div className={classes.vgrid}>
          {isLoading ? (
            <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'var(--text-3)', fontSize: '14px'}}>
              {t("Loading...")}
            </div>
          ) : filteredVideos.length === 0 ? (
            <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'var(--text-3)', fontSize: '14px'}}>
              {t("No video found. Try changing filters.")}
            </div>
          ) : (
            filteredVideos.map(v => {
              const levelName = getLevelName(v.video?.languageLevel);
              const catName = getCategoryName(v.video?.category);
              const thumbnail = v.video?.thumbnail;
              
              return (
                <div key={v.id} className={classes.vcard} onClick={() => navigate(`/lesson/${v.id}`)} style={{ cursor: 'pointer' }}>
                  <div className={classes.vthumb}>
                    {thumbnail ? (
                      <img src={thumbnail} alt={v.video?.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                    ) : (
                      <div className={classes.vthumbBg}></div>
                    )}
                    {levelName && <span className={classes.vlevel}>{levelName}</span>}
                    {catName && <span className={classes.vtopic}>{catName}</span>}
                    <button className={`${classes.vheart} ${likedVideos[v.id]?.liked ? classes.fav : ''}`} onClick={(e) => { e.stopPropagation(); toggleFav(v.id); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    </button>
                  </div>
                  
                  <div className={classes.vbody}>
                    <div className={classes.vtitle}>{v.video?.name}</div>
                    <div className={classes.vrow}>
                      {v.video?.words_count && <span className={classes.vwords}>{v.video.words_count} слів</span>}
                    </div>
                    
                    <div className={classes.vactions}>
                      <button className={`${classes.vaBtn} ${classes.vaStart}`} onClick={(e) => { e.stopPropagation(); navigate(`/lesson/${v.id}`); }}>Дивитись<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg></button>
                      
                      <div className={classes.vfeedback}>
                        <button className={`${classes.vfbBtn} ${likedVideos[v.id]?.liked ? classes.liked : ''}`} onClick={(e) => { e.stopPropagation(); setLike(v.id, 1); }} title="Подобається">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>
                        </button>
                        <button className={`${classes.vfbBtn} ${dislikedVideos[v.id] ? classes.disliked : ''}`} onClick={(e) => { e.stopPropagation(); setLike(v.id, -1); }} title="Не подобається">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg>
                        </button>
                      </div>
                      
                      <div className={classes.vaLikes}>
                        <button className={classes.vaLikeBtn} onClick={(e) => { e.stopPropagation(); }} title="Подобається">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>
                        </button>
                        <button className={classes.vaLikeBtn} onClick={(e) => { e.stopPropagation(); }} title="Не подобається">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg>
                        </button>
                      </div>
                      
                      <button className={classes.vaMenu} onClick={(e) => { e.stopPropagation(); setReportModal({open: true, id: v.id}); }} title="Меню">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* REPORT MODAL */}
      <div className={`${classes.modalOverlay} ${reportModal.open ? classes.show : ''}`} onClick={() => setReportModal({open: false, id: null})}>
        <div className={classes.modal} onClick={e => e.stopPropagation()}>
          <div className={classes.modalTitle}>{t("Report an issue")}</div>
          <div className={classes.modalText}>{t("What is wrong with this video?")}</div>
          <div className={classes.modalOpts}>
            {[t("Incorrect translation"), t("Incorrect video level"), t("Video does not work"), t("Other")].map((opt, i) => (
              <div key={i} className={`${classes.modalOpt} ${reportOpt === opt ? classes.selected : ''}`} onClick={() => setReportOpt(opt)}>
                {opt}
              </div>
            ))}
          </div>
          <textarea className={classes.modalTextarea} placeholder={t("Describe the issue (optional)")}></textarea>
          <div className={classes.modalBtns}>
            <button className={classes.mCancel} onClick={() => setReportModal({open: false, id: null})}>{t("Cancel")}</button>
            <button className={classes.mSubmit} onClick={submitReport}>{t("Send")}</button>
          </div>
        </div>
      </div>

      {/* TOAST */}
      <div className={`${classes.toast} ${toast ? classes.show : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Thank you! We will check this video.")}
      </div>
    </div>
  );
};

export default AllVideos;
